import {gql} from '@apollo/client';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {readableTime} from 'util/DateUtil';

import {
  SpreadsheetColumnsMappingDropdownQuery,
  SpreadsheetColumnsMappingDropdownQueryVariables,
} from './__generated/SpreadsheetColumnsMappingDropdownQuery';

export type SpreadsheetColumnsMapping =
  SpreadsheetColumnsMappingDropdownQuery['spreadsheetColumnsMappings'][number];

interface SpreadsheetColumnsMappingDropdownProps {
  tagSchemaId: string | null;
  assaySchemaId: string | null;
  uploadFlow: string;
  handleSelect: (spreadsheetColumnsMappings: SpreadsheetColumnsMapping) => void;
}

export const SPREADSHEET_COLUMNS_MAPPING_DROPDOWN_QUERY = gql`
  query SpreadsheetColumnsMappingDropdownQuery(
    $tagSchemaId: StrictString
    $assaySchemaId: StrictString
    $uploadFlow: StrictString
  ) @skipAudit {
    spreadsheetColumnsMappings(
      tagSchemaId: $tagSchemaId
      assaySchemaId: $assaySchemaId
      uploadFlow: $uploadFlow
    ) {
      ... on SpreadsheetColumnsMappingNode {
        ...SpreadsheetColumnsMappingGraphQL
      }
    }
  }
  ${getFragmentWrapper('SpreadsheetColumnsMapping').fragment}
`;

/**
 * Component that displays a searchable dropdown menu of spreadsheet column mappings. Will return null if
 * there are no mappings, or an error is encountered in the query.
 */
function QueryingSpreadsheetColumnsMappingDropdown(
  props: SpreadsheetColumnsMappingDropdownProps
): JSX.Element | null {
  const result = useBenchlingQuery<
    SpreadsheetColumnsMappingDropdownQuery,
    SpreadsheetColumnsMappingDropdownQueryVariables
  >(SPREADSHEET_COLUMNS_MAPPING_DROPDOWN_QUERY, {
    variables: {
      tagSchemaId: props.tagSchemaId,
      assaySchemaId: props.assaySchemaId,
      uploadFlow: props.uploadFlow,
    },
    fetchPolicy: 'cache-and-network',
  });

  switch (result.type) {
    case QueryResultType.ERROR: {
      return null;
    }
    case QueryResultType.PENDING:
      return <CenteredHorizontalThrobber />;
    case QueryResultType.SUCCESS: {
      const spreadsheetColumnsMappings = result.data.spreadsheetColumnsMappings;

      if (spreadsheetColumnsMappings.length === 0) {
        return null;
      }

      const optionDatas: Array<OptionData> = spreadsheetColumnsMappings.map((mapping) => ({
        name: mapping.name,
        value: mapping,
        metadata: `Created on ${readableTime(mapping.createdAt, {showTime: false})}`,
      }));

      return (
        <SelectDropdown
          closeOnInsideClick={true}
          displayName="Apply a column mapping"
          menu={
            <SearchableMenu
              width={MenuWidth.FULL}
              onSelect={props.handleSelect}
              optionDatas={optionDatas}
              // When selecting a mapping, the handler will be called but we will not persist the selected
              // mapping in the dropdown menu
              selectedValue={null}
            />
          }
          dropdownToggleOverride={
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              isDropdown={true}
              size={ButtonSize.SMALL}
              text="Apply a column mapping"
            />
          }
        />
      );
    }
    default:
      assertAndContinue(false, 'Unhandled query result type');
      return null;
  }
}
export default QueryingSpreadsheetColumnsMappingDropdown;
